/**
 * 「이 그림과 비슷한 것」을 묻는 재료 — 카드 한 장에서 뽑는다.
 *
 * ```
 *   광고 카드    → 광고 id      원장에 벡터가 이미 있다(그림을 다시 읽지 않는다)
 *   게시물 카드  → 게시물 id
 *   둘 다 없으면 → 섬네일 주소   상류가 그림을 받아 인코딩한다
 * ```
 * 🔑 **갈래마다 재료가 다르다** — 광고 카드의 «비슷한 게시물»은 id 가 없어 그림 주소로 묻고,
 *   게시물 카드의 «비슷한 광고»도 마찬가지다. 그래서 두 질문이 각자 재료를 고른다.
 * ⚠️ **상품 카드도 묻는다** — 흰 배경 제품컷은 색인(광고·소셜 소재)과 분포가 멀어 0건이 잦지만,
 *   0건이면 줄이 통째로 안 뜨므로 손해가 없다. ⛔ 상품 «끼리»의 유사 이미지는 상류에 창구가 없다
 *   (`/api/similar/image` 가 돌려주는 갈래는 `ads` · `posts` 둘뿐이다).
 * 🚨 **광고 쪽은 `clipFloor` 를 반드시 실어 보낸다** — 안 보내면 상류 기본값이 «거의 같은 소재»
 *   문턱이라 거의 늘 0건이다(`AD_CLIP_FLOOR`).
 */

import { AD_CLIP_FLOOR } from 'src/types/search-all/image-search';

import type { MediaCard } from './search-all-media-cards';

/** 무엇으로 물을지 — 채워진 것부터 쓴다 */
export type SimilarSubject = { adId: string; postId: string; imageUrl: string };

/**
 * @param card 카드 한 장
 * @returns 물을 수 있는 재료 · 그림도 id 도 없는 카드면 `null`
 */
export function toSimilarSubject(card: MediaCard | null | undefined): SimilarSubject | null {
  if (!card) return null;
  const isAd = card.kind === 'ad';
  const adId = isAd ? text(card.ad?.id) : '';
  const postId = isAd ? '' : text(card.post?.id);
  const imageUrl = /^https?:\/\//.test(card.thumb) ? card.thumb : '';
  if (!adId && !postId && !imageUrl) return null;
  return { adId, postId, imageUrl };
}

/** 비슷한 광고를 물을 수 있나 — 광고 id 도 그림도 없으면 물을 것이 없다 */
export function canAskSimilarAds(subject: SimilarSubject): boolean {
  return Boolean(subject.adId || subject.imageUrl);
}

/** 비슷한 게시물을 물을 수 있나 */
export function canAskSimilarPosts(subject: SimilarSubject): boolean {
  return Boolean(subject.postId || subject.imageUrl);
}

/** 비슷한 광고 질문 한 벌 */
export function similarAdsQuery(subject: SimilarSubject, limit: number) {
  const common = { limit, beautyOnly: true, clip: true, clipFloor: AD_CLIP_FLOOR };
  return subject.adId ? { adId: subject.adId, ...common } : { imageUrl: subject.imageUrl, ...common };
}

/** 비슷한 게시물 질문 한 벌 */
export function similarPostsQuery(subject: SimilarSubject, limit: number) {
  return subject.postId ? { postId: subject.postId, limit } : { imageUrl: subject.imageUrl, limit };
}

/** 같은 재료를 다시 묻지 않게 하는 열쇠 조각 — 질문에 실제로 실리는 것만 담는다 */
export function similarAdsKey(subject: SimilarSubject): string {
  return subject.adId ? `ad:${subject.adId}` : `img:${subject.imageUrl}`;
}

/** 같은 재료를 다시 묻지 않게 하는 열쇠 조각 */
export function similarPostsKey(subject: SimilarSubject): string {
  return subject.postId ? `post:${subject.postId}` : `img:${subject.imageUrl}`;
}

// ---- 이하 비공개 ----------------------------------------------------------

function text(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
